/**
 * Tax profile of an owner (backend modules/Accounting, S3).
 *
 * Endpoints under /api/accounting-owner-tax-settings/{owner}: GET, PUT,
 * POST taxation-method, GET taxation-method/history, PUT special-prepayments,
 * POST/DELETE oss-registrations, GET deadlines?year=.
 */

export enum FilingPeriodEnum {
  Monthly = "monthly",
  Quarterly = "quarterly",
}

export enum TaxationMethodEnum {
  /** Soll-Versteuerung (§ 16 UStG): recognised at the service/tax date. */
  Accrual = "accrual",
  /** Ist-Versteuerung (§ 20 UStG): recognised when the payment is received. */
  Cash = "cash",
}

export const GERMAN_FEDERAL_STATES = [
  "BW",
  "BY",
  "BE",
  "BB",
  "HB",
  "HH",
  "HE",
  "MV",
  "NI",
  "NW",
  "RP",
  "SL",
  "SN",
  "ST",
  "SH",
  "TH",
] as const;

export type GermanFederalState = (typeof GERMAN_FEDERAL_STATES)[number];

export interface AccountingOwnerTaxationMethodResource {
  id: string;
  owner_id: string;
  method: TaxationMethodEnum;
  valid_from: string;
  confirmed_by: string | null;
  confirmed_by_name?: string | null;
  confirmed_at: string | null;
  confirmation_text_version: number;
  note: string | null;
  applied_at: string | null;
  switch_summary: Record<string, string | number> | null;
  created_at: string | null;
}

export interface AccountingOwnerSpecialPrepayment {
  id: string;
  year: number;
  amount: string;
}

export interface AccountingOwnerOssRegistration {
  id: string;
  valid_from: string | null;
  valid_to: string | null;
  member_state_country_id: string;
}

export interface AccountingOwnerTaxSettingsResource {
  /** null while no profile is saved – the values are then a non-binding suggestion */
  id: string | null;
  /** false: nothing saved yet; only the explicit save creates the profile */
  is_saved: boolean;
  notice: string | null;
  owner_id: string;
  filing_period: FilingPeriodEnum;
  has_permanent_extension: boolean;
  special_prepayment_enabled: boolean;
  tax_office_number: string | null;
  elster_tax_number: string | null;
  /** Wirtschafts-Identifikationsnummer, Basiswert „DE“ und 9 Ziffern */
  w_idnr: string | null;
  federal_state: GermanFederalState | null;
  /** Address of the taxable person for the ELSTER Unternehmer block. */
  street: string | null;
  house_number: string | null;
  postal_code: string | null;
  city: string | null;
  /** Land der Unternehmeranschrift (Ländertabelle des ERP) */
  address_country_id: string | null;
  address_country?: { id: string; alpha2_code: string; name: string } | null;
  special_prepayments: AccountingOwnerSpecialPrepayment[];
  oss_registrations: AccountingOwnerOssRegistration[];
  /** null until the person has confirmed a method – exonn assumes none. */
  current_taxation_method: TaxationMethodEnum | null;
  taxation_method_history: AccountingOwnerTaxationMethodResource[];
  confirmation_text_version: number;
  updated_at: string | null;
}

export type UpdateAccountingOwnerTaxSettingsRequestBody = {
  filing_period: FilingPeriodEnum;
  has_permanent_extension: boolean;
  special_prepayment_enabled: boolean;
  tax_office_number?: string | null;
  elster_tax_number?: string | null;
  w_idnr?: string | null;
  federal_state?: GermanFederalState | null;
  street?: string | null;
  house_number?: string | null;
  postal_code?: string | null;
  city?: string | null;
  address_country_id?: string | null;
};

export type ConfirmTaxationMethodRequestBody = {
  method: TaxationMethodEnum;
  /** Y-m-d, first day of a filing period. */
  valid_from: string;
  confirmed: boolean;
  note?: string | null;
};

export interface FilingDeadline {
  period_year: number;
  period_month: number;
  period_label: string;
  period_start: string;
  period_end: string;
  due_date: string;
}
